"use client";

import Image from "next/image";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type TransitionEvent,
} from "react";
import {
  CorrectedAppCta,
  CorrectedFaq,
  CorrectedHero,
} from "@/modules/landing/components/CorrectedSharedSections";
import { nurseServiceSeoContent } from "@/shared/config/seo";
import styles from "./NurseServicePage.corrected.module.scss";

const assetRoot = "/assets/corrected/nurse";
const serviceAutoplayDelayMs = 2900;
const serviceBoundaryCloneCount = 3;

const assets = {
  hero: assetRoot + "/hero.webp",
  stats: [
    assetRoot + "/stat-families.webp",
    assetRoot + "/stat-nurses.webp",
    assetRoot + "/stat-visits.webp",
    assetRoot + "/stat-satisfaction.webp",
  ],
  why: [
    assetRoot + "/why-verified.webp",
    assetRoot + "/why-professional.webp",
    assetRoot + "/why-coordination.webp",
    assetRoot + "/why-personalized.webp",
    assetRoot + "/why-updates.webp",
    assetRoot + "/why-updates.webp",
  ],
  whyCenter: assetRoot + "/why-center.webp",
} as const;

const stats = [
  { value: "5000+", title: "Families Supported" },
  { value: "150+", title: "Verified Nurses" },
  { value: "2500+", title: "Home Visits" },
  { value: "98%", title: "Family Satisfaction" },
] as const;

const benefits = [
  {
    title: "Verified Home Nurses",
    copy: "Every nurse is verified, qualified, and background checked for your safety.",
  },
  {
    title: "Professional Care at Home",
    copy: "Expert nursing care delivered safely in the comfort of your home.",
  },
  {
    title: "Dedicated Care Coordination",
    copy: "Our care team supports scheduling and answers your questions.",
  },
  {
    title: "Personalized Care Plans",
    copy: "Care tailored to your health needs, recovery, and daily routine.",
  },
  {
    title: "Real-Time Family Updates",
    copy: "Get real-time updates via WhatsApp after every visit.",
  },
  {
    title: "Trusted & Transparent",
    copy: "Clear communication and reliable care every step of the way.",
  },
] as const;

const nursingServices = [
  {
    image: assetRoot + "/service-elderly-560.webp",
    title: "Elderly Nursing Care",
    copy: "Compassionate elderly nursing care to keep seniors healthy, comfortable, and independent.",
  },
  {
    image: assetRoot + "/service-post-hospital-560.webp",
    title: "Post-Hospital Care",
    copy: "Professional nursing care at home for a safe recovery after hospital discharge.",
  },
  {
    image: assetRoot + "/service-injection-iv-560.webp",
    title: "Injection & IV Therapy",
    copy: "Safe injection services and IV therapy at home by qualified nurses.",
  },
  {
    image: assetRoot + "/service-wound-dressing-560.webp",
    title: "Wound Dressing Care",
    copy: "Professional wound dressing and recovery support delivered safely at home.",
  },
  {
    image: assetRoot + "/service-catheter-tube-560.webp",
    title: "Catheter & Tube Care",
    copy: "Safe catheter and tube care from trained home nursing professionals.",
  },
  {
    image: assetRoot + "/service-chronic-560.webp",
    title: "Chronic Disease Management",
    copy: "Consistent nursing support for long-term conditions and changing care needs.",
  },
  {
    image: assetRoot + "/service-palliative-560.webp",
    title: "Palliative  Care",
    copy: "Compassionate support focused on comfort, dignity, and family peace of mind.",
  },
  {
    image: assetRoot + "/service-monitoring-560.webp",
    title: "Home Health Monitoring",
    copy: "Regular health monitoring and updates from qualified nurses at home.",
  },
] as const;

const carouselServices = [
  ...nursingServices.slice(-serviceBoundaryCloneCount),
  ...nursingServices,
  ...nursingServices.slice(0, serviceBoundaryCloneCount),
];

function normalizeServiceIndex(index: number) {
  return ((index % nursingServices.length) + nursingServices.length) % nursingServices.length;
}

const promiseItems = [
  { title: "Health Assessment", image: assetRoot + "/promise-health-160.webp" },
  { title: "Medication Support", image: assetRoot + "/promise-medication-160.webp" },
  { title: "Health Assessment", image: assetRoot + "/promise-health-alt-160.webp" },
  { title: "Care Documentation", image: assetRoot + "/promise-documentation-160.webp" },
  { title: "Family Updates", image: assetRoot + "/promise-updates-160.webp" },
  { title: "Compassionate Assistance", image: assetRoot + "/promise-compassion-160.webp" },
] as const;

const planFeatures = [
  "Health Assessment",
  "Medication Support",
  "Vital Signs Monitoring",
  "Wound Care",
  "Family Updates",
  "Dedicated Care Coordinator",
  "Personalized Care Plan",
  "24/7 Support",
] as const;

const plans = ["One-Time", "Short-Term", "Long-Term", "12 Hr", "24 Hr", "Custom"] as const;

const careSteps = [
  { image: assetRoot + "/step-needs-288.webp", title: "Tell Us Your Care Needs" },
  { image: assetRoot + "/step-nurse-288.webp", title: "Choose Your Nurse" },
  { image: assetRoot + "/step-care-288.webp", title: "Care Begins at Home" },
  { image: assetRoot + "/step-updated-288.webp", title: "Stay Updated" },
] as const;

const professionalHighlights = [
  "Professionally Verified",
  "Clinical Expertise",
  "Continuous Learning",
  "Compassionate Care",
] as const;

const professionals = [
  { firstLine: "GNM", secondLine: "Nurses", image: assetRoot + "/professional-gnm-120.webp" },
  {
    firstLine: "ICU & Critical",
    secondLine: "Care Nurses",
    image: assetRoot + "/professional-icu-120.webp",
  },
  {
    firstLine: "B.Sc Nursing",
    secondLine: "Professionals",
    image: assetRoot + "/professional-bsc-120.webp",
  },
  {
    firstLine: "Geriatric Care",
    secondLine: "Nurses",
    image: assetRoot + "/professional-geriatric-120.webp",
  },
  {
    firstLine: "Registered Nurses",
    secondLine: "(RN)",
    image: assetRoot + "/professional-rn-120.webp",
  },
  {
    firstLine: "Post-Surgical Care",
    secondLine: "Specialists",
    image: assetRoot + "/professional-post-surgical-120.webp",
  },
] as const;

const testimonials = [
  "Living in Singapore, I was worried about my father's recovery after surgery. Carevinco kept me updated after every visit, and the nurse was professional, caring, and punctual.",
  "Carevinco gave our family complete peace of mind with reliable nurses and transparent updates after every visit.",
  "The care team made every visit feel personal, dependable, and easy for our family to manage from abroad.",
] as const;

function Heading({ children, eyebrow }: { children: ReactNode; eyebrow: string }) {
  return (
    <div className={styles.heading}>
      <p className={styles.eyebrow}>{eyebrow}</p>
      <h2>{children}</h2>
    </div>
  );
}

function ArrowIcon({ direction }: { direction: "left" | "right" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d={direction === "left" ? "m15 18-6-6 6-6" : "m9 18 6-6-6-6"}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
}

function PlaybackIcon({ resume = false }: { resume?: boolean }) {
  return (
    <svg aria-hidden="true" fill="currentColor" viewBox="0 0 24 24">
      {resume ? <path d="m8 5 11 7-11 7V5Z" /> : <path d="M7 5h4v14H7V5Zm6 0h4v14h-4V5Z" />}
    </svg>
  );
}

function CheckIcon({ labelled = false }: { labelled?: boolean }) {
  return (
    <svg
      aria-hidden={labelled ? undefined : true}
      aria-label={labelled ? "Included" : undefined}
      role={labelled ? "img" : undefined}
      viewBox="0 0 24 24"
    >
      <path
        d="m5 12 4.2 4.2L19 6.5"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.4"
      />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="m12 2.8 2.8 5.7 6.3.9-4.6 4.4 1.1 6.3-5.6-3-5.6 3 1.1-6.3-4.6-4.4 6.3-.9L12 2.8Z" />
    </svg>
  );
}

function StatsStrip() {
  return (
    <section aria-label="Home nursing service metrics" className={`${styles.stats} ${styles.alignedSection}`}>
      <div className={styles.statsContainer}>
        <div className={styles.statsGrid}>
          {stats.map((stat, index) => (
            <article className={styles.stat} key={stat.title}>
              <Image alt="" height={83} src={assets.stats[index]} width={83} />
              <div>
                <strong>{stat.value}</strong>
                <span>{stat.title}</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function BenefitList({ end, start }: { end: number; start: number }) {
  return (
    <div className={styles.whyList}>
      {benefits.slice(start, end).map((benefit, relativeIndex) => {
        const index = start + relativeIndex;

        return (
          <article key={benefit.title}>
            <Image alt="" height={83} src={assets.why[index]} width={83} />
            <div>
              <h3>{benefit.title}</h3>
              <p>{benefit.copy}</p>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function WhyNurse() {
  return (
    <section className={[styles.section, styles.why, styles.alignedSection].join(" ")}>
      <div className="container">
        <Heading eyebrow="why families choose carevinco">
          Home Nursing Should Bring <span>Peace of Mind.</span>
        </Heading>
        <p className={styles.intro}>
          Finding the right nurse shouldn’t be stressful. Carevinco makes home nursing simple, transparent, and reassuring.
        </p>
        <div className={styles.whyGrid}>
          <div className={styles.whyListLeft}>
            <BenefitList end={3} start={0} />
          </div>
          <Image
            alt="Nurse caring for an elderly couple at home"
            className={styles.whyVisual}
            height={475}
            sizes="(max-width: 768px) calc(100vw - 36px), (max-width: 1024px) 360px, 460px"
            src={assets.whyCenter}
            width={485}
          />
          <div className={styles.whyListRight}>
            <BenefitList end={6} start={3} />
          </div>
        </div>
      </div>
    </section>
  );
}

function NursingServices() {
  const [renderedIndex, setRenderedIndex] = useState<number>(serviceBoundaryCloneCount);
  const [isJumping, setIsJumping] = useState(false);
  const [interactionPaused, setInteractionPaused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const jumpFrame = useRef<number | null>(null);
  const transitionInProgress = useRef(false);
  const activeIndex = normalizeServiceIndex(renderedIndex - serviceBoundaryCloneCount);
  const trackStyle = { "--service-index": renderedIndex } as CSSProperties;
  const isAutoRotating = !interactionPaused && !userPaused && !prefersReducedMotion;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => {
      transitionInProgress.current = false;
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  useEffect(
    () => () => {
      if (jumpFrame.current !== null) {
        window.cancelAnimationFrame(jumpFrame.current);
      }
      transitionInProgress.current = false;
    },
    [],
  );

  useEffect(() => {
    if (!isAutoRotating) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIsJumping(false);
      transitionInProgress.current = true;
      setRenderedIndex((value) => value + 1);
    }, serviceAutoplayDelayMs);

    return () => window.clearTimeout(timer);
  }, [isAutoRotating, renderedIndex]);

  function move(direction: number) {
    if (transitionInProgress.current && !prefersReducedMotion) {
      return;
    }

    setIsJumping(false);
    transitionInProgress.current = !prefersReducedMotion;
    setRenderedIndex((value) => {
      const nextIndex = value + direction;

      if (prefersReducedMotion) {
        return serviceBoundaryCloneCount + normalizeServiceIndex(nextIndex - serviceBoundaryCloneCount);
      }

      return nextIndex;
    });
  }

  function normalizeTrack(event: TransitionEvent<HTMLDivElement>) {
    if (event.currentTarget !== event.target || event.propertyName !== "transform") {
      return;
    }

    const firstOriginalIndex = serviceBoundaryCloneCount;
    const afterLastOriginalIndex = firstOriginalIndex + nursingServices.length;

    if (renderedIndex >= afterLastOriginalIndex || renderedIndex < firstOriginalIndex) {
      setIsJumping(true);
      setRenderedIndex(firstOriginalIndex + activeIndex);
      jumpFrame.current = window.requestAnimationFrame(() => {
        setIsJumping(false);
        transitionInProgress.current = false;
        jumpFrame.current = null;
      });
      return;
    }

    transitionInProgress.current = false;
  }

  return (
    <section className={[styles.section, styles.services, styles.alignedSection].join(" ")}>
      <div className={`${styles.servicesInner} container`}>
        <div className={styles.servicesCopy}>
          <p className={styles.eyebrow}>our nursing services</p>
          <h2 id="nursing-services-title">
            Comprehensive <span>Nursing</span> Care at Home.
          </h2>
          <p className={styles.intro}>
            Whether recovering from surgery, managing a medical condition, or supporting aging loved ones, our nursing
            services adapt to every family’s unique needs.
          </p>
          <button className={styles.primaryButton} data-open-inquiry type="button">
            Book a Care Visit
          </button>
        </div>

        <div
          aria-labelledby="nursing-services-title"
          aria-roledescription="carousel"
          className={styles.servicesCarousel}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setInteractionPaused(false);
            }
          }}
          onFocusCapture={() => setInteractionPaused(true)}
          onMouseEnter={() => setInteractionPaused(true)}
          onMouseLeave={() => setInteractionPaused(false)}
          role="region"
        >
          <div className={styles.servicesViewport} id="nursing-services-carousel">
            <div
              className={[styles.serviceTrack, isJumping ? styles.isJumping : ""].filter(Boolean).join(" ")}
              onTransitionEnd={normalizeTrack}
              style={trackStyle}
            >
              {carouselServices.map((service, index) => {
                const isClone =
                  index < serviceBoundaryCloneCount || index >= serviceBoundaryCloneCount + nursingServices.length;
                const serviceIndex = normalizeServiceIndex(index - serviceBoundaryCloneCount);

                return (
                  <article
                    aria-hidden={isClone}
                    aria-label={`${serviceIndex + 1} of ${nursingServices.length}`}
                    className={[
                      styles.serviceCard,
                      activeIndex === serviceIndex ? styles.serviceCardActive : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    key={service.title + "-" + index}
                  >
                    <div className={styles.serviceImage}>
                      <Image
                        alt=""
                        fill
                        sizes="(max-width: 480px) calc(100vw - 92px), (max-width: 768px) min(240px, calc(100vw - 96px)), 210px"
                        src={service.image}
                      />
                    </div>
                    <h3>{service.title}</h3>
                    <p>{service.copy}</p>
                  </article>
                );
              })}
            </div>
          </div>
          <p aria-live={isAutoRotating ? "off" : "polite"} className={styles.srOnly}>
            Showing {nursingServices[activeIndex].title}, service {activeIndex + 1} of {nursingServices.length}.
          </p>
          <div className={styles.carouselControls}>
            <button
              aria-controls="nursing-services-carousel"
              aria-label="Previous nursing service"
              className={styles.controlButton}
              onClick={() => move(-1)}
              type="button"
            >
              <ArrowIcon direction="left" />
            </button>
            <button
              aria-controls="nursing-services-carousel"
              aria-label="Next nursing service"
              className={styles.controlButton}
              onClick={() => move(1)}
              type="button"
            >
              <ArrowIcon direction="right" />
            </button>
            <button
              aria-controls="nursing-services-carousel"
              aria-label={
                prefersReducedMotion
                  ? "Automatic nursing service rotation disabled for reduced motion"
                  : userPaused
                    ? "Resume automatic nursing service rotation"
                    : "Pause automatic nursing service rotation"
              }
              aria-pressed={prefersReducedMotion || userPaused}
              className={styles.controlButton}
              disabled={prefersReducedMotion}
              onClick={() => setUserPaused((paused) => !paused)}
              type="button"
            >
              <PlaybackIcon resume={userPaused} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function CarePromise() {
  return (
    <section className={styles.promiseSection}>
      <div className={styles.promise}>
        <div className={styles.promiseTitle}>
          <p>every visit follows the carevinco promise</p>
          <h2>
            <span>Every Visit.</span> Delivered with Care.
          </h2>
        </div>
        <div className={styles.promiseItems}>
          {promiseItems.map((item, index) => (
            <article key={item.title + "-" + index}>
              <span className={styles.promiseIcon}>
                <Image alt="" fill sizes="80px" src={item.image} />
              </span>
              <strong>{item.title}</strong>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function CarePlans() {
  return (
    <section className={[styles.section, styles.plans, styles.alignedSection].join(" ")}>
      <div className="container">
        <Heading eyebrow="home care plans for families — carevinco">
          <span>Flexible Care Plans</span> Designed Around Your Needs
        </Heading>
        <div className={styles.tableWrap} role="region" aria-label="Care plan comparison" tabIndex={0}>
          <table>
            <thead>
              <tr>
                <th scope="col">Features</th>
                {plans.map((plan) => (
                  <th key={plan} scope="col">
                    {plan}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {planFeatures.map((feature, row) => (
                <tr key={feature}>
                  <th scope="row">{feature}</th>
                  {plans.map((plan) => (
                    <td key={plan}>
                      {row === 3 && plan === "One-Time" ? (
                        "Optional"
                      ) : row === 7 && plan === "Custom" ? (
                        "Optional"
                      ) : (
                        <CheckIcon labelled />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function SimpleSteps() {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(
      () => setActiveStep((value) => (value + 1) % careSteps.length),
      2200,
    );

    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className={[styles.section, styles.steps].join(" ")}>
      <Heading eyebrow="home nursing in">
        <span>4 Simple Steps</span>
      </Heading>
      <div className={styles.stepsGrid}>
        {careSteps.map((step, index) => (
          <article key={step.title}>
            <div className={styles.stepVisual}>
              <Image alt="" fill sizes="150px" src={step.image} />
              <b>{index + 1}</b>
            </div>
            <p>
              {step.title === "Choose Your Nurse" ? (
                <>
                  Choose
                  <br />
                  Your Nurse
                </>
              ) : step.title === "Care Begins at Home" ? (
                <>
                  Care Begins
                  <br />
                  at Home
                </>
              ) : step.title === "Stay Updated" ? (
                <>
                  Stay
                  <br />
                  Updated
                </>
              ) : (
                step.title
              )}
            </p>
            <span
              aria-hidden="true"
              className={[styles.stepConnector, index === activeStep ? styles.connectorActive : ""]
                .filter(Boolean)
                .join(" ")}
            />
          </article>
        ))}
      </div>
    </section>
  );
}

function Professionals() {
  return (
    <section className={[styles.section, styles.professionals].join(" ")}>
      <Heading eyebrow="qualified care professionals">
        Experienced Professionals.{" "}
        <span className={styles.professionalsLine}>
          <span>Compassionate</span> People.
        </span>
      </Heading>
      <div className={styles.professionalsBody}>
        <div className={styles.professionalsCopy}>
          <p>
            Every Carevinco professional is carefully selected, verified, and trained to deliver safe, personalized,
            and compassionate nursing care your family can trust.
          </p>
          <ul>
            {professionalHighlights.map((item) => (
              <li key={item}>
                <CheckIcon />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.professionalTags}>
          {professionals.map((professional) => (
            <article key={professional.firstLine + "-" + professional.secondLine}>
              <span className={styles.professionalIcon}>
                <Image alt="" fill sizes="60px" src={professional.image} />
              </span>
              <strong>
                {professional.firstLine}
                <br />
                {professional.secondLine}
              </strong>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const [active, setActive] = useState(0);
  const [interactionPaused, setInteractionPaused] = useState(false);
  const [userPaused, setUserPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener("change", updatePreference);
    return () => mediaQuery.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    if (interactionPaused || userPaused || prefersReducedMotion) {
      return undefined;
    }

    const timer = window.setInterval(
      () => setActive((value) => (value + 1) % testimonials.length),
      4500,
    );

    return () => window.clearInterval(timer);
  }, [interactionPaused, prefersReducedMotion, userPaused]);

  function next() {
    setActive((value) => (value + 1) % testimonials.length);
  }

  function previous() {
    setActive((value) => (value + testimonials.length - 1) % testimonials.length);
  }

  return (
    <section
      className={[styles.section, styles.testimonials].join(" ")}
      onBlurCapture={() => setInteractionPaused(false)}
      onFocusCapture={() => setInteractionPaused(true)}
      onMouseEnter={() => setInteractionPaused(true)}
      onMouseLeave={() => setInteractionPaused(false)}
    >
      <Heading eyebrow="families trust carevinco">
        Professional Care. <span>Proven</span>{" "}Every Day.
      </Heading>
      <p className={styles.intro}>
        Every visit builds trust. Here&apos;s what families say about their Carevinco experience.
      </p>
      <div className={styles.testimonialsCarousel}>
        <div
          className={styles.testimonialTrack}
          id="nurse-testimonials-carousel"
          style={{ transform: "translateX(calc(-" + active + " * var(--testimonial-slide)))" }}
        >
          {testimonials.map((quote) => (
            <blockquote key={quote}>
              <h3>Peace of Mind, Even From Abroad</h3>
              <p>“{quote}”</p>
              <footer>
                <span>Arun Kumar, Singapore</span>
                <span>
                  4.9 /5 <StarIcon />
                </span>
              </footer>
            </blockquote>
          ))}
        </div>
      </div>
      <p aria-live="polite" className={styles.srOnly}>
        Showing testimonial {active + 1} of {testimonials.length}.
      </p>
      <div className={styles.testimonialControls}>
        <button
          aria-controls="nurse-testimonials-carousel"
          aria-label="Previous testimonial"
          className={styles.controlButton}
          onClick={previous}
          type="button"
        >
          <ArrowIcon direction="left" />
        </button>
        <button
          aria-controls="nurse-testimonials-carousel"
          aria-label="Next testimonial"
          className={styles.controlButton}
          onClick={next}
          type="button"
        >
          <ArrowIcon direction="right" />
        </button>
        <button
          aria-label={prefersReducedMotion ? "Automatic testimonial rotation disabled for reduced motion" : userPaused ? "Resume testimonial rotation" : "Pause testimonial rotation"}
          aria-pressed={prefersReducedMotion || userPaused}
          className={styles.controlButton}
          disabled={prefersReducedMotion}
          onClick={() => setUserPaused((paused) => !paused)}
          type="button"
        >
          <PlaybackIcon resume={userPaused} />
        </button>
      </div>
    </section>
  );
}

export function NurseServicePage() {
  return (
    <main className={styles.page} id="service-nurse-services">
      <CorrectedHero
        balanceCopy
        breakTitle={false}
        description={nurseServiceSeoContent.description}
        imageSrc={assets.hero}
        showSteps={false}
        title={<>The Right Nurse. <span>Right at Home.</span></>}
      />
      <StatsStrip />
      <WhyNurse />
      <NursingServices />
      <CarePromise />
      <CarePlans />
      <SimpleSteps />
      <Professionals />
      <Testimonials />
      <CorrectedAppCta />
      <CorrectedFaq />
    </main>
  );
}
